/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useState} from 'react';
import {I18nextProvider} from 'react-i18next';
import {QueryClientProvider} from 'react-query';
import {ThemeProvider} from '@material-ui/core/styles';
import PageViewportMeta from '@ion/applications/src/components/PageViewportMeta';
import {ApplicationPage} from '@ion/applications/src/layout/types';
import {NetworkStatusPrompt} from '@ion/applications/src/prompts/NetworkStatusPrompt';
import StoreProvider from '@ion/applications/src/store/StoreProvider';
import {launchReduxDevTools} from '@ion/applications/src/store/tools';
import {isDevMode} from '@ion/applications/src/utils/env';
import {get, loadConfig} from '@ion/core/src/environment/environment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import i18n, {initializeLanguageAsync, localeHMR} from '@ion/core/src/locale/locale';
import {enable, info} from '@ion/core/src/logger/logger';
import {setArcGISConfig} from '@ion/core/src/services/ArcGISUtils';
import {setSDConnectConfig} from '@ion/core/src/services/SDConnectUtils';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {ErrorBoundary} from '@ion/dls/src/notifications/ErrorBoundary';
import {
  initResellerDevTools
} from '@ion/dls/src/theme/resellers/ResellerDevTools';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getQueryClient} from '@ion/query/src/config/client';
import {DevTools} from '@ion/query/src/tools/DevTools';
import {setPreAuthConfig} from '@ion/services/src/websocket/PreAuth';
import {initializeRouter, isDebug} from '@/routes/RouteDefinitions';
import applyConfigOverrides, {getTranslationsFilename} from '@/security/Resellers';
import rootReducer from '@/store/reducers';
import Layout from '../layout/Layout';
import {useApplicationTheme} from '../theme/useApplicationTheme';
import 'react-phone-number-input/style.css';
import '@ion/applications/src/styles/index.scss';
import '../fonts/fonts.scss';

const isDev = isDevMode();

if (isDev) {
  initResellerDevTools();
}

/**
 * Defines the root iOn Application, this component wraps all routes and injects
 * all providers, including i18Next and Material UI.
 */
function BMWPortal({Component, pageProps}: ApplicationPage) {
  const [initialized, setInitialized]       = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const theme: DLSTheme = useApplicationTheme();

  const layoutProps = useMemo(() => Component.getLayoutProps?.() || {}, [Component]);

  const isMounted = useHasMounted();

  useEffect(() => {
    async function initializeLanguage() {
      await initializeLanguageAsync(null, getTranslationsFilename());
      loadConfig(config => {
        applyConfigOverrides(config);
        setSDConnectConfig(config);
        setPreAuthConfig(config);
        setArcGISConfig(config);
        setInitialized(true);
      }, false);
      isDev && localeHMR();
      isDev && launchReduxDevTools();
    }
    if (isMounted && !initialized && !isInitializing) {
      setIsInitializing(true);
      enable(isDebug() || isDev);
      initializeRouter();
      info('Initializing Application ...');
      initializeLanguage();
    }
  }, [isMounted, initialized, isInitializing]);

  return (
    <>
      <PageViewportMeta />
      {initialized && (
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={getQueryClient()}>
              <ErrorBoundary>
                <StoreProvider rootReducer={rootReducer}>
                  {!layoutProps.omit ? (
                    <Layout {...layoutProps}>
                      <Component {...pageProps} />
                    </Layout>
                  ) : (
                    <Component {...pageProps}/>
                  )}
                </StoreProvider>
              </ErrorBoundary>
              <DevTools />
            </QueryClientProvider>
          </ThemeProvider>
        </I18nextProvider>
      )}
      {isMounted && !initialized && (
        <ThemeProvider theme={theme}>
          <ContentLoader width='60px' height='60px' fullPage />
        </ThemeProvider>
      )}
      <NetworkStatusPrompt />
    </>
  );
};
export default BMWPortal;
